import { TZDate } from '@date-fns/tz';
import { format } from 'date-fns';
import { z } from 'zod';
import { addDaysIso } from './calendar';
import { TIME_ZONE, type IsoDate } from './dates';

/** Нагадування стосується конкурсу або участі кандидата в конкурсі. */
export const REMINDER_SCOPES = ['contest', 'candidate'] as const;
export type ReminderScope = (typeof REMINDER_SCOPES)[number];

/** Час дня `HH:MM` (за Києвом). */
export const timeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Час у форматі ГГ:ХХ');

/**
 * Шаблон нагадування: «через N днів, о HH:MM, текст». Для конкурсів шаблони створюються
 * автоматично (дні — від дати оголошення), для кандидатів — це швидкі кнопки (дні — від сьогодні).
 */
export const reminderPresetSchema = z.object({
  days: z.number().int().min(0).max(365),
  text: z.string().trim().min(1, 'Обов’язкове поле').max(500),
  time: timeSchema,
});
export type ReminderPreset = z.infer<typeof reminderPresetSchema>;

/** Значення за замовчуванням для нагадування, яке HR створює вручну. */
export const reminderDefaultSchema = z.object({
  text: z.string().trim().min(1, 'Обов’язкове поле').max(500),
  time: timeSchema,
});
export type ReminderDefault = z.infer<typeof reminderDefaultSchema>;

export const reminderSettingsSchema = z.object({
  contest: z.object({
    presets: z.array(reminderPresetSchema).max(20),
    manual: reminderDefaultSchema,
  }),
  candidate: z.object({
    presets: z.array(reminderPresetSchema).max(20),
    manual: reminderDefaultSchema,
  }),
});
export type ReminderSettings = z.infer<typeof reminderSettingsSchema>;

/** Початкові налаштування: конкурс — 3, 7, 13 днів від оголошення. */
export const DEFAULT_REMINDER_SETTINGS: ReminderSettings = {
  contest: {
    presets: [
      { days: 3, text: 'Перевірити відгуки на вакансію', time: '10:00' },
      { days: 7, text: 'Обдзвонити кандидатів, запросити на співбесіду', time: '10:00' },
      { days: 13, text: 'Підбити підсумки: співбесіди, рішення щодо конкурсу', time: '10:00' },
    ],
    manual: { text: 'Перевірити конкурс', time: '10:00' },
  },
  candidate: {
    presets: [
      { days: 1, text: 'Передзвонити кандидату', time: '10:00' },
      { days: 3, text: 'Уточнити рішення кандидата', time: '10:00' },
      { days: 7, text: 'Нагадати про документи', time: '10:00' },
    ],
    manual: { text: 'Передзвонити кандидату', time: '10:00' },
  },
};

const target = z.union([
  z.object({ contestId: z.uuid(), applicationId: z.undefined().optional() }),
  z.object({ applicationId: z.uuid(), contestId: z.undefined().optional() }),
]);

export const reminderCreateSchema = z
  .object({
    text: z.string().trim().min(1, 'Обов’язкове поле').max(500),
    dueDate: z.iso.date(),
    dueTime: timeSchema,
  })
  .and(target);
export type ReminderCreateInput = z.input<typeof reminderCreateSchema>;

export const reminderUpdateSchema = z.object({
  id: z.uuid(),
  text: z.string().trim().min(1, 'Обов’язкове поле').max(500).optional(),
  dueDate: z.iso.date().optional(),
  dueTime: timeSchema.optional(),
  done: z.boolean().optional(),
});
export type ReminderUpdateInput = z.input<typeof reminderUpdateSchema>;

export const reminderListSchema = z.object({
  scope: z.enum(REMINDER_SCOPES),
  /** «Мої» — призначені поточному користувачу; «Усі» — усієї команди. */
  owner: z.enum(['mine', 'all']).default('mine'),
  status: z.enum(['active', 'done']).default('active'),
});
export type ReminderListInput = z.input<typeof reminderListSchema>;

export const reminderTargetSchema = target;

/** Дата нагадування з шаблону: від дати-основи (оголошення конкурсу або сьогодні). */
export function presetDueDate(base: IsoDate, preset: Pick<ReminderPreset, 'days'>): IsoDate {
  return addDaysIso(base, preset.days);
}

/** Поточні дата й час у Києві: `{ date: 'yyyy-MM-dd', time: 'HH:mm' }`. */
export function nowKyiv(now: Date = new Date()): { date: IsoDate; time: string } {
  const d = new TZDate(now, TIME_ZONE);
  return { date: format(d, 'yyyy-MM-dd'), time: format(d, 'HH:mm') };
}

export type ReminderBucket = 'overdue' | 'today' | 'tomorrow' | 'week' | 'later';

/** Група для показу: прострочені, сьогодні, завтра, найближчі 7 днів, пізніше. */
export function reminderBucket(
  due: { dueDate: IsoDate; dueTime: string },
  now: { date: IsoDate; time: string } = nowKyiv(),
): ReminderBucket {
  if (due.dueDate < now.date || (due.dueDate === now.date && due.dueTime <= now.time)) {
    return 'overdue';
  }
  if (due.dueDate === now.date) return 'today';
  if (due.dueDate === addDaysIso(now.date, 1)) return 'tomorrow';
  if (due.dueDate <= addDaysIso(now.date, 7)) return 'week';
  return 'later';
}
